import React, { useState, useEffect, Suspense } from 'react';
import { ProductInformation, ColoursWithLink, SizeSelect, CTAButtons, PartNumber } from '@ynap/product-details-page';
import {
    PromoPrice,
    SizeChartLink,
    ContactUsCTA,
    ShopMore,
    trackSizeGuideOpen,
    trackSizeSelect,
    trackEmailUsCTA,
    trackCallUsCTA,
    trackAccordion,
    trackAccordionLinkClick,
    trackViewMoreNavigation,
    trackSizeAndFitSizeSelect,
    trackSizeGuide,
    trackAddToBag,
    trackAddToBagError,
    upsellNavigation,
    trackAddToWishlist,
    trackAddToWishlistError,
    trackGoToBag,
    EditorialAccordion,
} from '../../../index';
import { OptimizelyFeature } from '@ynap/optimizely-utils';
import BEMHelper from '@ynap/bem-helper';
import { PriceWithSchema } from '@ynap/price';
import { SingleBadge, TransitionSingleBadge } from '@ynap/badge';
import { KlarnaMessage } from '../../Default/ProductDetails/KlarnaMessage';
import Notification from '@ynap/notification';
import LoyaltyBadgeNotification from '@ynap/loyalty-badge-notification';
import Skeleton from '@ynap/skeleton';
import CompanyInformation from '../../Default/CompanyInformation';
import DeliveryAndReturnsLegal from '../../../DeliveryAndReturns/DeliveryAndReturnsLegal';

// cant import this dunno why seems that version 1.3.7 was working
const promotionType = {
    MANUAL: 'manual',
    AUTO: 'auto',
    NONE: 'none',
} as const;

const PDPOverlay = React.lazy(() => import(/* webpackChunkName: "PDPOverlay" */ '../../Default/PDPOverlay'));

const bem = new BEMHelper('ProductDetails');

interface PersonalShopperDetails {
    imageURL: string;
}

interface UserSummary {
    isEip: boolean;
    loyaltyConsent: string;
    loyaltySegment: 'guest' | 'bronze' | 'silver' | 'gold' | 'eip' | 'eipplatinum';
    personalShopperDetails: PersonalShopperDetails;
}

interface SF {
    getUserSummary: () => Promise<UserSummary>; // Specify the correct type
}

declare global {
    interface Window {
        SF?: SF;
    }
}

const ProductDetails = (props) => {
    const {
        product,
        locale,
        messages,
        plpUrlTemplate,
        baseUrl,
        generateListingUrl,
        resetPLPState,
        promoPrice,
        ogMetaData,
        config = {},
        country,
        sizeGridFeatureToggle,
        analytics,
        features,
        actionsErrors: errors,
        changeSelectedSku,
        sizeLabelToggle,
        brandId,
        language,
        addToCart,
        addToWishlist,
        fetchWishlists,
        actionsLoading: { addToCartLoading, addToWishlistLoading, fetchWishlistsLoading },
        wishlists,
        redirectToWishlist,
        urlConfig,
        internal_urls,
        measurements,
        sizeAndFitInformation,
        customerCareUrl,
        mappedCategories,
        safeTrackEvent,
        deliveryReturns = {},
    } = props;

    const {
        head: {
            pdp: {
                stickyCTAButton = false,
                hideSizeConversionMessageByGender,
                notification: notificationConfig,
                enableMultipleWishlists,
                preventSkuNotification,
                includeCartWithAddToBag,
                enableDeliveryAndReturnCustomMessage,
                disableSizeChartForDesignerIdsAndCategories,
                companyInformationCountryCode,
                deliveryAndReturnLegalMessageEnabledCountries,
            } = {},
            plp: { facet: { ignoreCategories = [] } = {} } = {},
            minimumPriceCountries = [],
            errorNotificationTimer,
            tierExpansionEnabled,
        } = {},
    } = config;

    const {
        name,
        designerName,
        designerSeo,
        selectedSku,
        price,
        badges: { selectedProductColorFirstBadge, selectedProductSizeBadge } = {},
        saleExclusive,
        consideredBadge,
        buyable,
        showKlarnaMessage,
        selectedSize,
        sizeLabel,
        isOneSize,
        sizeConversion,
        sizeMeasurement,
        hasMeaningfulSize,
        optimizely,
        measurementType,
        colourSwatches,
        label: colourLabel,
        skus,
        sizeScheme,
        fjwThresholdHandler,
        tracking,
        madeIn,
        productNotes,
        fullfilledBy,
        consideredLabels,
        sustainabilityDisclaimers,
        finalSale,
        masterCategory,
        imageViews,
        imageTemplate,
        frontImage,
        sizeConversionBySku,
        designerIdentifier,
    } = product;

    // Modals
    const [modal, setModal] = useState<string>('');
    const [noSizeNotification, setNoSizeNotification] = useState<string | null>(null);
    const [userSummary, setUserSummary] = useState<UserSummary | undefined>(undefined);
    const [isSizeChartAndMessageLoading, setIsSizeChartAndMessageLoading] = useState<boolean>(true);
    const [shouldDisableSizeChartAndMessage, setShouldDisableSizeChartAndMessage] = useState<boolean>(false);
    const isEIP = userSummary?.isEip || false;
    const genderType = analytics?.page?.pageInfo?.gender;

    useEffect(() => {
        window?.SF?.getUserSummary?.().then((userSummary) => {
            setUserSummary(userSummary);
        });
    }, []);

    useEffect(() => {
        if (genderType && sizeScheme && designerIdentifier && isSizeChartAndMessageLoading) {
            setShouldDisableSizeChartAndMessage(
                disableSizeChartForDesignerIdsAndCategories?.[genderType?.toLowerCase()]?.[designerIdentifier]?.includes(sizeScheme) || false,
            );
            setIsSizeChartAndMessageLoading(false);
        } else if (!disableSizeChartForDesignerIdsAndCategories) {
            setShouldDisableSizeChartAndMessage(false);
            setIsSizeChartAndMessageLoading(false);
        }
    }, [genderType, sizeScheme, designerIdentifier]);

    const { marketingProperties, promotionApplied = promotionType.NONE } = promoPrice;
    const hasPromoBadge = promotionApplied === promotionType.MANUAL;
    const hasAutoApplyPromo = promotionApplied === promotionType.AUTO;
    const { loyaltySegment, loyaltyConsent, personalShopperDetails: { imageURL } = {} } = userSummary ?? {};
    const hasPromoPrice = hasPromoBadge ? 'promoPriceApplied' : '';

    const showMinimumPriceLabel = minimumPriceCountries.includes(country?.toUpperCase?.()) && typeof price?.minimumPrice === 'number';
    const isEUcountry = minimumPriceCountries.includes(country?.toUpperCase?.());

    const stickyModifier = stickyCTAButton ? 'stickyCta' : '';

    const sizeConversionBadge =
        sizeLabel &&
        selectedSize &&
        messages.sizes.sizeConversion({
            sizeLabel: selectedSku?.size?.centralSizeLabel ? selectedSku.size.centralSizeLabel : sizeLabel,
            localLanguageType: selectedSize.name,
            localSizeValue:
                selectedSize.labels.length > 1 ? `${selectedSize.labels[0]}-${selectedSize.labels[selectedSize.labels.length - 1]}` : selectedSize.labels[0],
        });
    const isStockBadgesVisible = !!(sizeGridFeatureToggle && selectedProductSizeBadge?.label);
    const sizeConversionBadgeDesignersIncluded = hideSizeConversionMessageByGender?.[genderType]?.includes(designerName);
    const isSizeConversionBadgeVisible = !!(sizeConversionBadge && selectedSize && !sizeConversionBadgeDesignersIncluded);

    const showColour = !features?.hideColour;
    const selectedProduct = {
        personalShopperNotification: buyable?.notification?.contactUsCTA,
        eipPersonalShopperNotification: buyable?.notification?.eip?.contactUsCTA,
        name: product.name,
        type: buyable?.notification?.type,
        oneSize: skus && skus.length === 1,
        isWatchExperts: features?.isWatchExperts,
    };

    const { emailUsCTA = null, isWatchExperts, callUsCTA = null } = features;

    const showContactUsCTA = !emailUsCTA?.Component && (buyable?.notification?.contactUsCTA || buyable?.notification?.eip?.contactUsCTA);

    //helm config converts strings with commas into arrays. This functions turns it back to a string
    const formatNotificationMessage = (message) => {
        if (Array.isArray(message)) {
            return message.join();
        } else {
            return message;
        }
    };

    const trackedAddToBag = (sku, { setActionNotificationOnError, addToCartLoading, reservationId, includeShoppingCart }) => {
        const {
            addToCart,
            product: { sizeLabel, size },
        } = props;

        return addToCart(sku, { setActionNotificationOnError, addToCartLoading, reservationId, includeShoppingCart })
            .then((response) => {
                if (response && response.type === 'ADD_TO_CART_SUCCESS') {
                    return safeTrackEvent(trackAddToBag, props, sku, sizeLabel, null, size.schemas);
                } else {
                    return safeTrackEvent(trackAddToBagError, props, response.type);
                }
            })
            .catch((e) => e); //Catch any errors thrown from state
    };

    const trackedAddToBagError = () => {
        safeTrackEvent(trackAddToBagError, props, 'NO_SIZE_SELECTED');
    };

    const trackedGoToBag = () => {
        const { redirectToCheckout } = props;
        safeTrackEvent(trackGoToBag, props);
        return redirectToCheckout(locale);
    };

    const cartButton = addToCart && { addToCart: trackedAddToBag, failedAddToBag: trackedAddToBagError, goToCart: trackedGoToBag, addToCartLoading };

    const trackedAddToWishlist = (sku, wishlistId) => {
        const {
            addToWishlist,
            selectedSizeLabel,
            product: { size },
            optimizely,
        } = props;
        return addToWishlist(sku, wishlistId)
            .then(() => {
                /* istanbul ignore next */
                optimizely?.track('plp_pdp_atw');
                /* istanbul ignore next */
                optimizely?.track('pdp_addtowishlist');
                return safeTrackEvent(trackAddToWishlist, props, sku, selectedSizeLabel, size.schemas);
            })
            .catch((error) => {
                return safeTrackEvent(trackAddToWishlistError, props, error.type);
            }); //Catch any errors thrown from state
    };

    const trackedAddToWishlistError = () => {
        safeTrackEvent(trackAddToWishlistError, props, 'NO_SIZE_SELECTED');
    };

    const wishlistButton = addToWishlist && {
        fetchWishlists,
        fetchWishlistsLoading,
        addToWishlist: trackedAddToWishlist,
        failedAddToWishlist: trackedAddToWishlistError,
        addToWishlistLoading,
        wishlists,
        goToWishlist: redirectToWishlist,
    };

    const isProductSizeDisplayable = !!skus?.length;
    const showCallUsCTA = callUsCTA?.Component && !emailUsCTA?.Component && !showContactUsCTA;

    const shouldShowLoyaltyLink = !!(userSummary !== undefined && tierExpansionEnabled);
    const isExclusiveBadge = selectedProductColorFirstBadge?.key === 'BADGE_EXCLUSIVE';
    const tier = loyaltyConsent === 'true' && loyaltySegment ? loyaltySegment : 'guest';
    const loyaltyNotification = messages?.loyalty?.notification({ tier, locale: typeof locale === 'string' ? locale : locale.id, isExclusiveBadge }) || '';
    const isCompanyInformationLinkEnabled = country?.includes(companyInformationCountryCode);
    const showDeliveryAndReturnsLegal = deliveryAndReturnLegalMessageEnabledCountries?.includes(country?.toUpperCase?.());

    return (
        <div data-testid="productInfoContainer">
            <div className={bem('productInfoStickyWrapper')} data-testid="productInfoStickyWrapper">
                <ProductInformation
                    className={{ container: bem('productInformation') }}
                    productId={product.colourPartNumber}
                    skuId={selectedSku && selectedSku.partNumber}
                    name={name}
                    designerName={designerName}
                    designerSeo={designerSeo}
                    locale={locale}
                    plpUrlTemplate={plpUrlTemplate}
                    messages={messages}
                    generateListingUrl={generateListingUrl}
                    baseUrl={baseUrl}
                    designerLinkOnClick={() => resetPLPState()}
                    promoPriceApplied={hasPromoPrice}
                />
                <PriceWithSchema
                    offerUrl={ogMetaData?.url}
                    details
                    {...price}
                    className={bem('price', [stickyModifier, hasPromoPrice])}
                    messages={messages}
                    locale={locale}
                    priceSuffix={messages.price?.taxIncluded && messages.price.taxIncluded()}
                    setModal={setModal}
                    showMinimumPriceLabel={showMinimumPriceLabel}
                    isEUcountry={isEUcountry}
                    designerName={designerName}
                    badgeKey={selectedProductColorFirstBadge.key}
                    country={country}
                    addMicrodata
                    saleExclusive={saleExclusive}
                    promoPriceApplied={hasPromoPrice} // TODO this state can be derived from promotionApplied
                    promotionApplied={promotionApplied}
                    marketingProperties={marketingProperties}
                    hasAutoApplyPromo={hasAutoApplyPromo}
                />
                {/* TODO: to check with a product with sale */}
                <div className={bem('promoInformation', { hasAutoApplyPromo })}>
                    <PromoPrice messages={messages} price={price} promotionApplied={promotionApplied} marketingProperties={marketingProperties} />
                </div>

                {selectedProductColorFirstBadge && !hasPromoPrice && (
                    <TransitionSingleBadge badge={selectedProductColorFirstBadge} stickyModifier={stickyModifier} promoPriceApplied={hasPromoPrice} />
                )}

                {hasPromoPrice && selectedProductSizeBadge && <TransitionSingleBadge badge={selectedProductSizeBadge} stickyModifier={stickyModifier} />}

                {consideredBadge && (
                    <SingleBadge
                        className={bem('consideredBadge')}
                        considered
                        badge={{ label: consideredBadge, key: 'CONSIDERED_BADGE' }}
                        translation={messages.considered.badge()}
                    />
                )}
                <div className={bem('klarnaMessageVariant2', { hasPromoBadge }, 'klarnaMessage__container')}>
                    <KlarnaMessage
                        messages={messages}
                        country={country}
                        config={config}
                        showKlarnaMessage={showKlarnaMessage}
                        buyable={buyable}
                        price={price}
                        locale={locale}
                    />
                </div>
                {(isStockBadgesVisible || isSizeConversionBadgeVisible) && (
                    <div className={bem('stockSizeConversionBadgesWrapper')}>
                        {isStockBadgesVisible && <SingleBadge className={bem('stockBadges')} badge={selectedProductSizeBadge} hasBackGround={false} />}

                        {isSizeConversionBadgeVisible && !shouldDisableSizeChartAndMessage && (
                            <SingleBadge className={bem('sizeConversion')} badge={{ label: sizeConversionBadge }} hasBackGround={false} />
                        )}
                    </div>
                )}
                {showColour && (
                    <ColoursWithLink
                        className={bem('colours')}
                        messages={messages}
                        colours={colourSwatches}
                        colourLabel={colourLabel}
                        onClick={(_, position) =>
                            safeTrackEvent(upsellNavigation, props, product, position, 'view more colours in this product', 'alt colours')
                        }
                    />
                )}

                {shouldDisableSizeChartAndMessage ? null : (
                    <OptimizelyFeature feature={['WEB00552_SizeTiles_PDP']}>
                        {(featureProps) => {
                            const variation = featureProps?.variationKey;

                            return isSizeChartAndMessageLoading ? (
                                <Skeleton count={1} containerClassName={bem('sizeChartLinkSkeletonContainer')} className={bem('sizeChartLinkSkeleton')} />
                            ) : (
                                <SizeChartLink
                                    setSizeChartModal={(e) => {
                                        e.preventDefault();
                                        safeTrackEvent(trackSizeGuideOpen, props);
                                        setModal('sizeChart');
                                        optimizely?.track?.('click_view_size_guide');
                                    }}
                                    isOneSize={isOneSize}
                                    sizeConversion={sizeConversion}
                                    sizeMeasurement={sizeMeasurement}
                                    hasMeaningfulSize={hasMeaningfulSize}
                                    optimizely={optimizely}
                                    placeModifier={(variation === 'v1_web00552_exposed' && 'placeModifier') || null}
                                >
                                    {measurementType === 'iiMeasurements'
                                        ? messages.sizes.sizeGuide.measuringGuide()
                                        : messages.sizes.sizeGuide.viewSizeGuide()}
                                </SizeChartLink>
                            );
                        }}
                    </OptimizelyFeature>
                )}
                <SizeSelect
                    className={bem('sizeSelect', [selectedProduct.oneSize && 'isOneSize'])}
                    skus={skus}
                    value={sizeLabel}
                    price={price}
                    messages={messages}
                    promptError={errors && (!!errors.wishlist || !!errors.addToCart)}
                    changeSelectedSku={(sizeSku, sizeLabel) => {
                        changeSelectedSku(sizeSku, sizeLabel);
                        safeTrackEvent(trackSizeSelect, props, sizeSku.partNumber, sizeLabel, sizeSku.size.schemas);
                    }}
                    displayLabel={sizeLabelToggle}
                    sizeGridFeatureToggle={sizeGridFeatureToggle}
                    noSizeNotification={noSizeNotification}
                    sizeConversionBySku={product.sizeConversionBySku}
                    sizeConversionMessage={''}
                    disableOneSizeLeft={false}
                    alwaysGrid={false}
                    sizeTiles={false}
                    sizeScheme={sizeScheme}
                    hasMeaningfulSize={hasMeaningfulSize}
                    features={features}
                />
                {notificationConfig?.enabled && notificationConfig?.locales.includes(locale) && (
                    <Notification type="info" className={bem('pdpNotification')} message={formatNotificationMessage(notificationConfig.message[language])} />
                )}

                <CTAButtons
                    buttonsWrapperClassName={bem('ctaButtonsWrapper')}
                    cart={cartButton}
                    category={tracking?.primaryCategory?.child?.label}
                    className={bem('ctaButtons', [shouldShowLoyaltyLink && 'hideMargin'])}
                    disableAddToBag={!buyable?.canAddToBag || !isProductSizeDisplayable}
                    errorNotificationTimer={errorNotificationTimer}
                    errors={errors}
                    features={features}
                    fjwThresholdHandler={fjwThresholdHandler}
                    includeCartWithAddToBag={includeCartWithAddToBag}
                    isProductSizeDisplayable={isProductSizeDisplayable}
                    locale={locale}
                    messages={messages}
                    mrpHandler
                    multipleWishlistsFeatureToggle={!!enableMultipleWishlists}
                    napHandler
                    preventSkuNotification={preventSkuNotification}
                    selectedProduct={selectedProduct}
                    selectedProductVariant={product}
                    setModal={setModal}
                    setNoSizeNotification={setNoSizeNotification}
                    showCallUsCTA={showCallUsCTA}
                    skuPartNumber={selectedSku && selectedSku.partNumber}
                    stickyCTAButton={stickyModifier}
                    trackCallUsCTA={() => safeTrackEvent(trackCallUsCTA, props)}
                    trackEmailUsCTA={() => safeTrackEvent(trackEmailUsCTA, props, 'help with watch purchase')}
                    urlConfig={urlConfig}
                    wishlist={wishlistButton}
                />
                {shouldShowLoyaltyLink && (
                    <LoyaltyBadgeNotification className={bem('loyaltyMessage')} tier={tier} tracking={props.tracking} isExclusiveBadge={isExclusiveBadge} />
                )}

                {showContactUsCTA && (
                    <ContactUsCTA
                        className={bem('contactUsCTATop', { addMarginTop: !loyaltyNotification })}
                        selectedProduct={selectedProduct}
                        isWatchExperts={isWatchExperts}
                        brandId={brandId}
                        imageUrl={imageURL}
                        trackEvent={tracking.trackEvent}
                    />
                )}
                {showDeliveryAndReturnsLegal && (
                    <DeliveryAndReturnsLegal
                        className={bem('deliveryAndReturnsLegal')}
                        coreMediaMessage={deliveryReturns}
                        locale={locale}
                        config={config}
                        messages={messages}
                        setModal={setModal}
                    />
                )}
                <EditorialAccordion
                    className={bem('editorialAccordion')}
                    onOpen={(accordion) => {
                        safeTrackEvent(trackAccordion, props, accordion);
                        if (accordion === 'Size & Fit') {
                            /* istanbul ignore next */
                            optimizely?.track?.('interaction_size_fit_accordion');
                        }
                    }}
                    onLinkClick={(targetURL) => {
                        // This feature only exists for ton
                        /* istanbul ignore next */
                        safeTrackEvent(trackAccordionLinkClick, props, targetURL);
                    }}
                    modifier="pdpAccordion"
                    setSizeChartModal={(e) => {
                        e.preventDefault();
                        safeTrackEvent(trackSizeGuideOpen, props);
                        setModal('sizeChart');
                    }}
                    features={features}
                    madeIn={madeIn}
                    productNotes={productNotes}
                    sizeConversion={sizeConversion}
                    sizeMeasurement={sizeMeasurement}
                    internal_urls={internal_urls}
                    locale={locale}
                    skus={skus}
                    measurements={measurements}
                    sizeAndFitInformation={sizeAndFitInformation}
                    customerCareUrl={customerCareUrl}
                    isOneSize={isOneSize}
                    selectedPartNumber={product.colourPartNumber}
                    fullfilledBy={fullfilledBy}
                    sizeDetails={messages.sizeAndFit}
                    hasMeaningfulSize={hasMeaningfulSize}
                    // This feature only exists for ton and relates with onLinkClick
                    considered={false}
                    consideredLabels={consideredLabels}
                    sustainabilityDisclaimers={sustainabilityDisclaimers}
                    finalSale={finalSale}
                    selectedSize={selectedSize}
                    disableOneSizeLeft={false}
                    sizeLabel={sizeLabel}
                    masterCategory={masterCategory}
                    isEip={isEIP}
                    enableDeliveryAndReturnCustomMessage={enableDeliveryAndReturnCustomMessage}
                    country={country}
                    colourPartNumber={product.colourPartNumber}
                    messages={messages}
                    imageViews={imageViews}
                    imageTemplate={imageTemplate}
                    designerIdentifier={designerIdentifier}
                    shouldDisableSizeChartAndMessage={shouldDisableSizeChartAndMessage}
                />
                <PartNumber className={bem('partNumber')} selectedPartNumber={product.colourPartNumber} messages={messages} />
                {isCompanyInformationLinkEnabled && <CompanyInformation className={bem('companyInformation')} messages={messages} />}
                <ShopMore
                    className={bem('shopMore')}
                    messages={messages}
                    product={product}
                    mappedCategories={mappedCategories}
                    generateListingUrl={generateListingUrl}
                    plpUrlTemplate={plpUrlTemplate}
                    locale={locale}
                    linkOnClick={() => {
                        resetPLPState();
                        safeTrackEvent(trackViewMoreNavigation, props);
                    }}
                    ignoreCategories={ignoreCategories}
                />
                {modal && (
                    <Suspense fallback={''}>
                        <PDPOverlay
                            name={name}
                            frontImage={frontImage}
                            skus={skus}
                            sizeLabel={sizeLabel}
                            designerName={designerName}
                            content={modal}
                            {...props}
                            setModal={setModal}
                            sizeAndFitInformation={sizeAndFitInformation}
                            sizeMeasurement={sizeMeasurement}
                            sizeConversion={sizeConversion}
                            sizeConversionBySku={sizeConversionBySku}
                            masterCategory={masterCategory}
                            trackSizeAndFitSizeSelect={trackSizeAndFitSizeSelect}
                            safeTrackEvent={(buildAnalyticsFunc) => safeTrackEvent(buildAnalyticsFunc, props)}
                            trackSizeGuide={trackSizeGuide}
                            measurementType={measurementType}
                            sizeScheme={sizeScheme}
                            isEip={isEIP}
                        />
                    </Suspense>
                )}
            </div>
        </div>
    );
};

export default ProductDetails;
